import React, { useEffect, useState, useCallback, useMemo } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission, isOwner } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck, Lock, ChevronDown, History, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const ACTIONS = ["create", "view", "edit", "delete"];
const ACTION_LABEL = { create: "Create", view: "View", edit: "Edit", delete: "Delete" };

/**
 * Reusable "?" tooltip that renders a permission row's per-action help.
 * `hints` is the `action_hints` map from the backend catalog
 * (e.g. { create: "…", view: "…", edit: "…", delete: "…" }).
 * Falls back to `null` if the row has no hints so managers only see
 * the icon on rows that actually have guidance.
 */
function PermissionHelpTooltip({ label, hints }) {
  if (!hints || Object.keys(hints).length === 0) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          aria-label={`Help for ${label}`}
          data-testid={`perm-help-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs text-left">
        <div className="text-xs font-semibold mb-1">{label}</div>
        <ul className="text-xs space-y-1">
          {ACTIONS.filter((a) => hints[a]).map((a) => (
            <li key={a}>
              <b>{ACTION_LABEL[a]}:</b> {hints[a]}
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Custom Role & Permission Matrix (embedded inside the Staff Logins page).
 *
 * The permission catalog is a 3-level tree (group → item → action). The
 * editor renders each group as a collapsible section with a 4-column
 * (Create / View / Edit / Delete) matrix. Rows whose parent's `view` is
 * off — currently only "Branch Settings" — hide their child items.
 */
export default function BrandRolesInline() {
  const { staff, refreshStaff } = useAuth();
  const [schema, setSchema] = useState(null);
  const [roles, setRoles] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canManage = hasPermission(staff, "roles.manage_roles");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, a] = await Promise.all([
        staffApi.get("/permissions/schema"),
        staffApi.get("/roles"),
        staffApi.get("/roles/audit").catch(() => ({ data: [] })),
      ]);
      setSchema(s.data);
      setRoles(r.data || []);
      setAudit(a.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    if (!schema) return;
    const emptyPerms = {};
    for (const g of schema.catalog) {
      for (const it of g.items) {
        for (const act of it.actions || []) emptyPerms[`${it.key}.${act}`] = false;
        for (const sa of it.scope_actions || []) emptyPerms[`${it.key}.${sa.key}`] = false;
      }
    }
    setEditing({ id: null, name: "", hierarchy_level: 10, permissions: emptyPerms, is_locked: false, is_owner: false });
    setDialogOpen(true);
  };
  const openEdit = (role) => {
    if (role.is_owner) { toast.info("The Owner role is immutable and cannot be edited."); return; }
    if (role.is_locked) { toast.info(`The ${role.name} role is locked and cannot be edited.`); return; }
    setEditing({ ...role, permissions: { ...(role.permissions || {}) } });
    setDialogOpen(true);
  };
  const openView = (role) => {
    // Read-only preview for locked / owner roles so operators can still
    // audit exactly what those roles carry.
    setEditing({ ...role, _readonly: true, permissions: { ...(role.permissions || {}) } });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { toast.error("Role name is required."); return; }
    const payload = {
      name: editing.name.trim(),
      hierarchy_level: Number(editing.hierarchy_level) || 0,
      permissions: editing.permissions,
    };
    try {
      if (editing.id) {
        await staffApi.put(`/roles/${editing.id}`, payload);
        toast.success("Role updated");
      } else {
        await staffApi.post("/roles", payload);
        toast.success("Role created");
      }
      setDialogOpen(false);
      await load();
      // Refresh the currently signed-in staff in case they were editing
      // their own role and just changed their permissions.
      if (refreshStaff) await refreshStaff().catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeRole = async (role) => {
    if (role.is_owner) return toast.error("The Owner role is immutable.");
    if (role.is_locked) return toast.error(`The ${role.name} role is locked and cannot be deleted.`);
    if (!window.confirm(`Delete role "${role.name}"? Staff currently on this role must be reassigned first.`)) return;
    try {
      await staffApi.delete(`/roles/${role.id}`);
      toast.success("Role deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!canManage) {
    return (
      <Card className="rounded-lg"><CardContent className="p-8 text-sm text-muted-foreground" data-testid="roles-no-permission">You don&apos;t have permission to manage roles.</CardContent></Card>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-2xl">Create custom roles, choose which branches they can operate on, and toggle exactly what each role is allowed to <b>create</b>, <b>view</b>, <b>edit</b>, or <b>delete</b>. The Owner and Admin roles are locked.</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAudit((v) => !v)} data-testid="roles-audit-toggle" className="gap-2"><History className="w-4 h-4" /> Audit Log</Button>
          <Button onClick={openNew} disabled={!schema} data-testid="new-role-btn" className="gap-2"><Plus className="w-4 h-4" /> New role</Button>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="grid gap-3">
        {roles.map((r) => (
          <Card key={r.id} className="rounded-lg" data-testid={`role-row-${r.id}`}>
            <CardContent className="p-5 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {(r.is_owner || r.is_locked) ? <Lock className="w-4 h-4 text-amber-600" /> : <ShieldCheck className="w-4 h-4 text-primary" />}
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    {r.name}
                    {r.is_preset && !r.is_owner && <Badge variant="outline" className="rounded-full text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">Preset</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">Level {r.hierarchy_level} · {Object.values(r.permissions || {}).filter(Boolean).length} perms</p>
                </div>
                {r.is_owner && <Badge variant="outline" className="rounded-full text-[10px] bg-amber-50 text-amber-800 border-amber-200" data-testid={`role-immutable-${r.id}`}>Owner · Immutable</Badge>}
                {!r.is_owner && r.is_locked && <Badge variant="outline" className="rounded-full text-[10px] bg-amber-50 text-amber-800 border-amber-200" data-testid={`role-locked-${r.id}`}>Locked</Badge>}
              </div>
              <div className="flex items-center gap-2">
                {(r.is_owner || r.is_locked) ? (
                  <Button size="sm" variant="ghost" onClick={() => openView(r)} data-testid={`view-role-${r.id}`}>View</Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-role-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => removeRole(r)} disabled={r.is_owner || r.is_locked} data-testid={`delete-role-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {roles.length === 0 && !loading && (
          <Card className="rounded-lg"><CardContent className="p-8 text-sm text-muted-foreground text-center">No roles yet.</CardContent></Card>
        )}
      </div>

      {showAudit && (
        <Card className="rounded-lg mt-6" data-testid="roles-audit-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3"><History className="w-4 h-4 text-muted-foreground" /><span className="text-sm font-medium">Role audit trail</span></div>
            {audit.length === 0 && <p className="text-sm text-muted-foreground">No audit events yet.</p>}
            <div className="divide-y divide-border">
              {audit.map((row) => (
                <div key={row.id} className="py-2 text-sm flex items-center justify-between gap-2" data-testid={`audit-row-${row.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="rounded-full text-[10px] uppercase tracking-wider">{row.kind}</Badge>
                    <span className="truncate">{row.description}</span>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">{row.by_name} · {new Date(row.at).toLocaleString("en-GB")}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <RoleEditor
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        setEditing={setEditing}
        schema={schema}
        onSave={save}
        actorIsOwner={isOwner(staff)}
        actorLevel={staff?.hierarchy_level || 0}
      />
    </div>
  );
}


function RoleEditor({ open, onOpenChange, editing, setEditing, schema, onSave, actorIsOwner, actorLevel }) {
  const readOnly = !!editing?._readonly;

  const ownerOnly = useMemo(() => {
    if (!schema) return new Set();
    return new Set(schema.owner_only_grant || []);
  }, [schema]);

  // Track which groups are expanded; default all open on mount.
  const [openGroups, setOpenGroups] = useState({});
  useEffect(() => {
    if (schema) {
      const initial = {};
      for (const g of schema.catalog) initial[g.key] = true;
      setOpenGroups(initial);
    }
  }, [schema]);

  if (!editing || !schema) return null;

  const setPerm = (key, value) => {
    setEditing((prev) => ({ ...prev, permissions: { ...(prev.permissions || {}), [key]: value } }));
  };

  const groupAllKeys = (group) => {
    const out = [];
    for (const item of group.items) {
      for (const act of item.actions || []) {
        if ((item.disabled_actions || {})[act]) continue;
        const k = `${item.key}.${act}`;
        if (ownerOnly.has(k) && !actorIsOwner) continue;
        out.push(k);
      }
      // Also cover scope-action toggles (Personal / Global View on Booking
      // Calendar) so Select-all-in-group ticks them too.
      for (const sa of item.scope_actions || []) {
        const k = `${item.key}.${sa.key}`;
        if (ownerOnly.has(k) && !actorIsOwner) continue;
        out.push(k);
      }
    }
    return out;
  };

  const groupSelectAll = (group, value) => {
    const keys = groupAllKeys(group);
    setEditing((prev) => {
      const next = { ...(prev.permissions || {}) };
      for (const k of keys) next[k] = value;
      return { ...prev, permissions: next };
    });
  };

  const groupState = (group) => {
    const keys = groupAllKeys(group);
    if (keys.length === 0) return "empty";
    const on = keys.filter((k) => (editing.permissions || {})[k]).length;
    if (on === 0) return "none";
    if (on === keys.length) return "all";
    return "some";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="role-editor-dialog">
        <DialogHeader>
          <DialogTitle>
            {readOnly ? `View role · ${editing.name}` : (editing.id ? "Edit role" : "New role")}
          </DialogTitle>
          <DialogDescription>
            Choose which permissions this role can Create, View, Edit, or Delete. Branch access is set per staff member in the Staff Logins tab.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <div>
            <Label>Role name</Label>
            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Regional Manager" data-testid="role-name-input" disabled={readOnly} />
          </div>
          <div>
            <Label>Hierarchy level</Label>
            <Input type="number" min={0} max={actorLevel} value={editing.hierarchy_level} onChange={(e) => setEditing({ ...editing, hierarchy_level: e.target.value })} data-testid="role-level-input" disabled={readOnly} />
            <p className="text-[11px] text-muted-foreground mt-1">Max {actorLevel} (your own level). No role can be assigned above the creator&apos;s level.</p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {schema.catalog.map((group) => {
            const state = groupState(group);
            const isOpen = openGroups[group.key];
            return (
              <Collapsible key={group.key} open={isOpen} onOpenChange={(v) => setOpenGroups((prev) => ({ ...prev, [group.key]: v }))} className="border border-border rounded-lg" data-testid={`perm-group-${group.key}`}>
                <div className="flex items-center gap-2 p-3 bg-muted/40">
                  <CollapsibleTrigger asChild>
                    <button type="button" className="flex items-center gap-2 min-w-0 flex-1 text-left" data-testid={`perm-group-toggle-${group.key}`}>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                      <ShieldCheck className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold">{group.label}</span>
                      <Badge variant="outline" className="rounded-full text-[10px] ml-2">
                        {state === "all" ? "All" : state === "some" ? "Partial" : "None"}
                      </Badge>
                    </button>
                  </CollapsibleTrigger>
                  {!readOnly && (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => groupSelectAll(group, true)} data-testid={`perm-group-select-all-${group.key}`}>Select all</Button>
                      <Button size="sm" variant="ghost" onClick={() => groupSelectAll(group, false)} data-testid={`perm-group-unselect-all-${group.key}`}>Unselect all</Button>
                    </div>
                  )}
                </div>
                <CollapsibleContent>
                  <PermMatrix
                    group={group}
                    permissions={editing.permissions}
                    onChange={setPerm}
                    ownerOnly={ownerOnly}
                    actorIsOwner={actorIsOwner}
                    readOnly={readOnly}
                  />
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="role-editor-cancel">{readOnly ? "Close" : "Cancel"}</Button>
          {!readOnly && (
            <Button onClick={onSave} data-testid="role-editor-save">Save role</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PermMatrix({ group, permissions, onChange, ownerOnly, actorIsOwner, readOnly }) {
  const parentKey = group.parent_key || null;
  const parentViewOn = parentKey ? !!(permissions || {})[`${parentKey}.view`] : true;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="text-left p-2 pl-4 font-medium">Permission</th>
            {ACTIONS.map((act) => (
              <th key={act} className="text-center p-2 font-medium w-24">{ACTION_LABEL[act]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {group.items.map((item) => {
            const isParent = !!item.is_parent;
            const isSubchild = parentKey && !isParent;
            const dim = isSubchild && !parentViewOn;
            const rowClass = `border-b border-border/60 last:border-b-0 ${dim ? "opacity-40" : ""} ${isParent ? "bg-primary/5" : ""}`;
            const rows = [
              <tr
                key={item.key}
                className={rowClass}
                data-testid={`perm-row-${item.key.replace(/\./g, "-")}`}
              >
                <td className="p-2 pl-4">
                  <div className="flex items-center gap-2">
                    {isSubchild && <span className="text-muted-foreground text-xs">↳</span>}
                    <span className={isParent ? "font-medium" : ""}>{item.label}</span>
                    <PermissionHelpTooltip label={item.label} hints={item.action_hints} />
                    {isParent && <Badge variant="outline" className="rounded-full text-[10px] bg-primary/10 text-primary border-primary/20">Parent gate</Badge>}
                  </div>
                  {isSubchild && !parentViewOn && (
                    <p className="text-[11px] text-muted-foreground mt-1">Grant &quot;View&quot; on {group.label} to enable this row.</p>
                  )}
                </td>
                {ACTIONS.map((act) => {
                  const supported = (item.actions || []).includes(act);
                  const disabledReason = (item.disabled_actions || {})[act];
                  const k = `${item.key}.${act}`;
                  const isOwnerOnly = ownerOnly.has(k);
                  const lockedByOwner = isOwnerOnly && !actorIsOwner;
                  const on = !!(permissions || {})[k];
                  if (!supported || disabledReason) {
                    return (
                      <td key={act} className="p-2 text-center text-muted-foreground" title={disabledReason || ""} data-testid={`perm-cell-${item.key.replace(/\./g, "-")}-${act}`}>
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md text-xs">—</span>
                      </td>
                    );
                  }
                  return (
                    <td key={act} className="p-2 text-center" data-testid={`perm-cell-${item.key.replace(/\./g, "-")}-${act}`}>
                      <div className="inline-flex items-center gap-1">
                        <Checkbox
                          checked={on}
                          onCheckedChange={(v) => onChange(k, !!v)}
                          disabled={readOnly || lockedByOwner || (dim && !isParent)}
                          data-testid={`perm-check-${item.key.replace(/\./g, "-")}-${act}`}
                        />
                        {isOwnerOnly && <Lock className="w-3 h-3 text-amber-600" title="Only the Owner can grant this permission" />}
                      </div>
                    </td>
                  );
                })}
              </tr>,
            ];
            // Scope-actions sub-row (currently only Booking Calendar's
            // Personal View / Global View). Rendered as a compact strip
            // that spans the action columns so the extra toggles feel
            // like an extension of the row above rather than a new item.
            if ((item.scope_actions || []).length > 0) {
              rows.push(
                <tr key={`${item.key}-scope`} className={`border-b border-border/60 last:border-b-0 ${dim ? "opacity-40" : ""} bg-muted/20`} data-testid={`perm-scope-row-${item.key.replace(/\./g, "-")}`}>
                  <td className="p-2 pl-10 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <span>↳ View scope</span>
                      <Badge variant="outline" className="rounded-full text-[10px]">Calendar</Badge>
                    </div>
                  </td>
                  <td colSpan={ACTIONS.length} className="p-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      {item.scope_actions.map((sa) => {
                        const k = `${item.key}.${sa.key}`;
                        const isOwnerOnly = ownerOnly.has(k);
                        const lockedByOwner = isOwnerOnly && !actorIsOwner;
                        const on = !!(permissions || {})[k];
                        return (
                          <label key={sa.key} className={`flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs ${readOnly || dim ? "opacity-70" : "hover:bg-background cursor-pointer"}`} data-testid={`perm-scope-cell-${item.key.replace(/\./g, "-")}-${sa.key}`}>
                            <Checkbox
                              checked={on}
                              onCheckedChange={(v) => onChange(k, !!v)}
                              disabled={readOnly || lockedByOwner || dim}
                              data-testid={`perm-scope-check-${item.key.replace(/\./g, "-")}-${sa.key}`}
                            />
                            <span className="font-medium">{sa.label}</span>
                            {sa.hint && <span className="text-muted-foreground">· {sa.hint}</span>}
                          </label>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              );
            }
            return rows;
          })}
        </tbody>
      </table>
    </div>
  );
}
